import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useLang } from "@/lib/useLang";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WIDTH = 1000;
const HEIGHT = 220;
const PAD = { top: 16, right: 16, bottom: 36, left: 48 };

function fmtDayLabel(iso, lang) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(
      lang === "ml" ? "ml-IN" : "en-IN",
      { month: "short", day: "numeric" },
    );
  } catch {
    return iso;
  }
}

function fmtCount(n, lang) {
  if (n >= 100000) return `${(n / 100000).toFixed(n % 100000 === 0 ? 0 : 1)}${lang === "ml" ? "ല" : "L"}`;
  if (n >= 1000) return `${(n / 1000).toFixed(n % 1000 === 0 ? 0 : 1)}K`;
  return n.toLocaleString();
}

/**
 * Daily vote-velocity sparkline.
 * Pulls /api/daily-votes and renders a brutal-style area chart:
 *   - Black baseline, saffron filled area, black stroke
 *   - Peak-day callout marker
 *   - X-axis: first / peak / last day labels only (no clutter)
 *   - Y-axis: max + half-max gridlines
 */
export const VelocitySparkline = () => {
  const { t, lang } = useLang();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let alive = true;
    axios
      .get(`${API}/daily-votes`)
      .then((r) => {
        if (alive) setData(r.data);
      })
      .catch(() => {
        if (alive) setError(true);
      });
    return () => {
      alive = false;
    };
  }, []);

  const chart = useMemo(() => {
    if (!data?.days || data.days.length < 2) return null;
    const days = data.days;
    const maxCount = Math.max(...days.map((d) => d.count), 1);
    const innerW = WIDTH - PAD.left - PAD.right;
    const innerH = HEIGHT - PAD.top - PAD.bottom;
    const step = days.length > 1 ? innerW / (days.length - 1) : 0;

    const points = days.map((d, i) => {
      const x = PAD.left + i * step;
      const y = PAD.top + (1 - d.count / maxCount) * innerH;
      return { x, y, count: d.count, date: d.date };
    });

    const linePath = points
      .map((p, i) => `${i === 0 ? "M" : "L"}${p.x.toFixed(1)},${p.y.toFixed(1)}`)
      .join(" ");

    const areaPath =
      `M${points[0].x.toFixed(1)},${(PAD.top + innerH).toFixed(1)} ` +
      points
        .map((p) => `L${p.x.toFixed(1)},${p.y.toFixed(1)}`)
        .join(" ") +
      ` L${points[points.length - 1].x.toFixed(1)},${(PAD.top + innerH).toFixed(1)} Z`;

    const peakIdx = days.reduce(
      (best, d, i) => (d.count > days[best].count ? i : best),
      0,
    );

    return {
      points,
      linePath,
      areaPath,
      peakIdx,
      maxCount,
      innerH,
      innerW,
      step,
      days,
    };
  }, [data]);

  return (
    <section className="space-y-4 fade-up" data-testid="velocity-sparkline">
      <h2
        className="font-display uppercase leading-none"
        style={{ fontSize: "clamp(1.75rem, 4vw, 2.75rem)" }}
        data-testid="sparkline-heading"
      >
        {t("sparkline_heading")}
      </h2>
      <p
        className="text-sm md:text-base font-bold tracking-wide text-neutral-700"
        data-testid="sparkline-sub"
      >
        {t("sparkline_sub")}
      </p>

      <div className="brutal-card p-4 md:p-6">
        {error || !chart ? (
          <p
            className="text-sm uppercase font-bold tracking-[0.3em] text-neutral-600 py-8"
            data-testid="sparkline-empty"
          >
            {t("sparkline_empty")}
          </p>
        ) : (
          <>
            <div className="w-full overflow-x-auto">
              <svg
                viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
                role="img"
                aria-label={t("sparkline_sub")}
                style={{ width: "100%", height: "auto", display: "block" }}
                data-testid="sparkline-svg"
              >
                {/* Gridlines */}
                <line
                  x1={PAD.left}
                  x2={WIDTH - PAD.right}
                  y1={PAD.top}
                  y2={PAD.top}
                  stroke="#0A0A0A"
                  strokeOpacity="0.15"
                  strokeWidth="1"
                  strokeDasharray="4 4"
                />
                <line
                  x1={PAD.left}
                  x2={WIDTH - PAD.right}
                  y1={PAD.top + chart.innerH / 2}
                  y2={PAD.top + chart.innerH / 2}
                  stroke="#0A0A0A"
                  strokeOpacity="0.15"
                  strokeWidth="1"
                  strokeDasharray="4 4"
                />
                {/* Baseline */}
                <line
                  x1={PAD.left}
                  x2={WIDTH - PAD.right}
                  y1={PAD.top + chart.innerH}
                  y2={PAD.top + chart.innerH}
                  stroke="#0A0A0A"
                  strokeWidth="3"
                />
                {/* Y-axis labels */}
                <text
                  x={PAD.left - 8}
                  y={PAD.top + 4}
                  textAnchor="end"
                  fontFamily="Anton, 'Work Sans', sans-serif"
                  fontSize="14"
                  fill="#0A0A0A"
                >
                  {fmtCount(chart.maxCount, lang)}
                </text>
                <text
                  x={PAD.left - 8}
                  y={PAD.top + chart.innerH / 2 + 4}
                  textAnchor="end"
                  fontFamily="Anton, 'Work Sans', sans-serif"
                  fontSize="14"
                  fill="#0A0A0A"
                  opacity="0.6"
                >
                  {fmtCount(Math.round(chart.maxCount / 2), lang)}
                </text>
                <text
                  x={PAD.left - 8}
                  y={PAD.top + chart.innerH + 4}
                  textAnchor="end"
                  fontFamily="Anton, 'Work Sans', sans-serif"
                  fontSize="14"
                  fill="#0A0A0A"
                >
                  0
                </text>

                {/* Area fill (saffron) */}
                <path
                  d={chart.areaPath}
                  fill="#FF9933"
                  fillOpacity="0.45"
                />
                {/* Stroke (black) */}
                <path
                  d={chart.linePath}
                  fill="none"
                  stroke="#0A0A0A"
                  strokeWidth="3"
                  strokeLinejoin="miter"
                  strokeLinecap="square"
                />

                {/* Peak marker */}
                {chart.points[chart.peakIdx] && (
                  <g data-testid="sparkline-peak-marker">
                    <line
                      x1={chart.points[chart.peakIdx].x}
                      x2={chart.points[chart.peakIdx].x}
                      y1={chart.points[chart.peakIdx].y}
                      y2={PAD.top + chart.innerH}
                      stroke="#E3000F"
                      strokeWidth="2"
                      strokeDasharray="3 3"
                    />
                    <circle
                      cx={chart.points[chart.peakIdx].x}
                      cy={chart.points[chart.peakIdx].y}
                      r="7"
                      fill="#E3000F"
                      stroke="#0A0A0A"
                      strokeWidth="3"
                    />
                    <text
                      x={Math.min(
                        Math.max(chart.points[chart.peakIdx].x, PAD.left + 70),
                        WIDTH - PAD.right - 70,
                      )}
                      y={Math.max(chart.points[chart.peakIdx].y - 14, PAD.top + 12)}
                      textAnchor="middle"
                      fontFamily="Anton, 'Work Sans', sans-serif"
                      fontSize="16"
                      fill="#0A0A0A"
                      fontWeight="700"
                    >
                      {fmtCount(chart.days[chart.peakIdx].count, lang)} ·{" "}
                      {fmtDayLabel(chart.days[chart.peakIdx].date, lang)}
                    </text>
                  </g>
                )}

                {/* X-axis: first / peak / last day labels */}
                <text
                  x={chart.points[0].x}
                  y={PAD.top + chart.innerH + 22}
                  textAnchor="start"
                  fontFamily="'Work Sans', sans-serif"
                  fontSize="13"
                  fontWeight="700"
                  fill="#0A0A0A"
                >
                  {fmtDayLabel(chart.days[0].date, lang)}
                </text>
                <text
                  x={chart.points[chart.points.length - 1].x}
                  y={PAD.top + chart.innerH + 22}
                  textAnchor="end"
                  fontFamily="'Work Sans', sans-serif"
                  fontSize="13"
                  fontWeight="700"
                  fill="#0A0A0A"
                >
                  {fmtDayLabel(chart.days[chart.days.length - 1].date, lang)}
                </text>
              </svg>
            </div>

            {/* Legend below */}
            <div className="flex flex-wrap gap-x-6 gap-y-2 mt-4 text-xs uppercase font-bold tracking-[0.25em] text-neutral-700">
              <span className="flex items-center gap-2" data-testid="sparkline-peak-label">
                <span
                  aria-hidden="true"
                  className="inline-block border-2 border-black"
                  style={{ background: "#E3000F", width: 12, height: 12 }}
                />
                {t("sparkline_peak_day")}:{" "}
                <span data-testid="sparkline-peak-value">
                  {fmtCount(chart.days[chart.peakIdx].count, lang)} ·{" "}
                  {fmtDayLabel(chart.days[chart.peakIdx].date, lang)}
                </span>
              </span>
              <span className="flex items-center gap-2">
                <span
                  aria-hidden="true"
                  className="inline-block border-2 border-black"
                  style={{ background: "#FF9933", width: 12, height: 12 }}
                />
                {t("sparkline_y_axis_label")}
              </span>
            </div>
          </>
        )}
      </div>
    </section>
  );
};

export default VelocitySparkline;
